import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { admin, palette, radii, typography } from '../../../../styles/tokens';
import type { ParsedSheet } from './types';

interface UploadStepProps {
  onParsed: (parsed: ParsedSheet) => void;
}

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

const HEADER_FIELD_HINTS = [
  'name', 'first', 'last', 'phone', 'mobile', 'cell',
  'email', 'e-mail', 'client', 'customer',
];

function detectHasHeader(rows: string[][]): boolean {
  if (rows.length === 0) return false;
  const first = rows[0];
  if (first.length === 0) return false;

  // Any numeric-looking values in row 0? Then probably not a header.
  const anyNumeric = first.some(cell => {
    const trimmed = (cell ?? '').trim();
    if (!trimmed) return false;
    return /^-?\d+(\.\d+)?$/.test(trimmed);
  });
  if (anyNumeric) return false;

  // Any cell resembling a known field hint?
  const anyHint = first.some(cell => {
    const lc = (cell ?? '').trim().toLowerCase();
    return HEADER_FIELD_HINTS.some(h => lc.includes(h));
  });
  if (anyHint) return true;

  // Short tokens and no whitespace-heavy strings — lean header.
  const allShort = first.every(cell => (cell ?? '').trim().length > 0 && (cell ?? '').trim().length <= 32);
  return allShort;
}

async function parseCsv(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: results => {
        const rows = (results.data as unknown as string[][])
          .map(r => (Array.isArray(r) ? r.map(c => String(c ?? '')) : []))
          .filter(r => r.some(c => c.trim() !== ''));
        resolve(rows);
      },
      error: err => reject(err),
    });
  });
}

async function parseExcel(file: File): Promise<string[][]> {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const sheet = wb.worksheets[0];
  if (!sheet) return [];

  // Produce the same output shape as the CSV branch: string[][], with rows
  // that are entirely empty dropped. ExcelJS columns are 1-indexed; use the
  // sheet's own columnCount so we preserve gaps.
  const colCount = sheet.columnCount;
  const out: string[][] = [];
  sheet.eachRow({ includeEmpty: false }, row => {
    const cells: string[] = [];
    for (let c = 1; c <= colCount; c++) {
      const cell = row.getCell(c);
      const value = cell.value;
      if (value == null) {
        cells.push('');
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        cells.push(String(value));
      } else if (value instanceof Date) {
        cells.push(value.toISOString().slice(0, 10));
      } else if (typeof value === 'object') {
        // Formula results, rich text, hyperlinks, error objects.
        const asResult = (value as { result?: unknown }).result;
        if (asResult != null) {
          cells.push(String(asResult));
        } else {
          const asText = (value as { text?: unknown }).text;
          if (asText != null) {
            cells.push(String(asText));
          } else {
            cells.push(cell.text ?? '');
          }
        }
      } else {
        cells.push(String(value));
      }
    }
    if (cells.some(c => c.trim() !== '')) out.push(cells);
  });

  return out;
}

export default function UploadStep({ onParsed }: UploadStepProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);

    if (file.size > MAX_BYTES) {
      setError('This file is larger than 10 MB. Try splitting it or saving a smaller export.');
      return;
    }

    const lower = file.name.toLowerCase();
    const isCsv = lower.endsWith('.csv');
    const isXlsx = lower.endsWith('.xlsx') || lower.endsWith('.xls');
    if (!isCsv && !isXlsx) {
      setError("This file doesn't look like a spreadsheet. Try saving it as .csv from Excel or Google Sheets.");
      return;
    }

    setBusy(true);
    try {
      const rows = isCsv ? await parseCsv(file) : await parseExcel(file);
      if (rows.length === 0) {
        setError('This file is empty — no rows to import.');
        setBusy(false);
        return;
      }
      onParsed({
        rows,
        hasHeader: detectHasHeader(rows),
        fileName: file.name,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`We couldn't read this file. ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p
        style={{
          margin: 0,
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
        }}
      >
        Drop a CSV or Excel file below. We&rsquo;ll figure out the columns and
        let you review everything before anything lands in Clients.
      </p>

      <div
        onDragOver={e => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={e => {
          e.preventDefault();
          setDragActive(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragActive ? palette.accent : palette.g200}`,
          background: dragActive ? palette.accentWash : palette.g50,
          borderRadius: radii.md,
          padding: 48,
          textAlign: 'center',
          cursor: busy ? 'progress' : 'pointer',
          transition: 'all 150ms ease',
        }}
      >
        <Upload size={32} color={palette.accent} strokeWidth={1.75} />
        <div
          style={{
            marginTop: 12,
            fontSize: typography.size.heading,
            fontWeight: typography.weight.semibold,
            color: admin.header,
          }}
        >
          {busy ? 'Reading your file…' : 'Drop a file here, or click to browse'}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: typography.size.caption,
            color: admin.caption,
          }}
        >
          CSV, XLSX, or XLS. Up to 10 MB.
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={e => handleFile(e.target.files?.[0] ?? undefined)}
          style={{ display: 'none' }}
        />
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            background: palette.redWash,
            border: `1px solid ${palette.red}`,
            borderRadius: radii.sm,
            color: palette.red,
            fontSize: typography.size.caption,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
